import { usePageMeta } from "@/hooks/usePageMeta";
import ContactForm from "@/pages/about/components/ContactForm";

const USAGE_STEPS = [
  {
    icon: "ri-search-2-line",
    title: "搜索与浏览",
    desc: "在顶部搜索框输入影片名称、原名或演员，也可以在首页与分类页直接浏览推荐内容。",
  },
  {
    icon: "ri-filter-3-line",
    title: "多条件筛选",
    desc: "分类页支持按类型、地区、年份区间多选筛选，并可按上映时间、评分、热度排序。",
  },
  {
    icon: "ri-heart-3-line",
    title: "收藏想看的影片",
    desc: "在详情页点击收藏，影片会保存在你的浏览器本地，无需注册登录，刷新后依然可见。",
  },
  {
    icon: "ri-play-circle-line",
    title: "关于播放说明",
    desc: "详情页播放区域为界面演示，本站不提供实际视频流媒体服务，仅用于展示排版与交互。",
  },
];

export default function About() {
  usePageMeta(
    "关于我们 - 欧美精品在线观看",
    "了解欧美精品在线观看网站的定位与发展背景、内容范围、使用说明、联系方式与版权声明。",
    "/about"
  );

  return (
    <div className="w-full">
      <section className="relative w-full overflow-hidden">
        <div className="relative h-[320px] w-full md:h-[400px]">
          <img
            src="https://readdy.ai/api/search-image?query=elegant%20dark%20cinema%20auditorium%20with%20rows%20of%20empty%20seats%20and%20a%20large%20glowing%20blank%20screen%2C%20warm%20amber%20accent%20lights%20along%20the%20aisle%2C%20deep%20blue%20shadows%2C%20cinematic%20atmosphere&width=1600&height=700&seq=about-01&orientation=landscape"
            alt="影院放映厅场景"
            title="关于欧美精品在线观看"
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/70 to-background-950/40" />
          <div className="absolute inset-0 flex items-center">
            <div className="mx-auto w-full max-w-[1400px] px-4 md:px-8">
              <nav aria-label="面包屑" className="flex items-center gap-1 text-xs text-foreground-300">
                <span>首页</span>
                <i className="ri-arrow-right-s-line" />
                <span className="text-foreground-100">关于我们</span>
              </nav>
              <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-50 md:text-5xl">
                关于我们
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-200 md:text-base">
                一个专注欧美影视资料整理与推荐的展示平台，让每一次查找都清晰、专业、愉快。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1400px] px-4 pt-12 md:px-8 md:pt-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-50 md:text-2xl">
              发展背景与定位
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-300 md:text-base">
              随着欧美影视作品在国内观众中的热度持续上升，海量的片单与分散的信息让查找变得困难。
              欧美精品在线观看由此诞生，我们希望把最新上线的欧美电影与电视剧，和历久弥新的经典作品，
              整理成一份条理清晰的片单，让观众可以更高效地发现想看的内容。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-400">
              平台定位为“影视资料与推荐展示站”，不做下载与播放中转，只专注于内容组织、
              信息呈现与观看指引，力求成为欧美影视爱好者值得信赖的参考入口。
            </p>
            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { value: "40+", label: "收录影视" },
                { value: "20+", label: "内容类型" },
                { value: "4", label: "主题专题" },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-lg border border-background-800 bg-background-900 px-3 py-4 text-center"
                >
                  <p className="font-heading text-xl font-bold text-accent-400 md:text-2xl">
                    {item.value}
                  </p>
                  <p className="mt-1 text-xs text-foreground-400">{item.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-50 md:text-2xl">
              使用说明
            </h2>
            <div className="mt-4 space-y-3">
              {USAGE_STEPS.map((step) => (
                <div
                  key={step.title}
                  className="flex gap-4 rounded-lg border border-background-800 bg-background-900 p-4"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-500/15">
                    <i className={`${step.icon} text-lg text-accent-400`} />
                  </span>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-100">{step.title}</h3>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-400">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1400px] px-4 pt-12 md:px-8 md:pt-16">
        <div className="grid grid-cols-1 gap-8 rounded-lg border border-background-800 bg-background-900 p-6 lg:grid-cols-[1fr_1.2fr] md:p-10">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-50 md:text-2xl">
              联系我们
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-400">
              如果你对内容有建议、发现影片信息有误，或希望与我们合作，欢迎通过以下方式联系我们。
            </p>

            <div className="mt-6 space-y-3">
              <div className="flex items-center gap-3 rounded-md border border-background-800 bg-background-950 px-4 py-3">
                <i className="ri-mail-line text-lg text-accent-400" />
                <div>
                  <p className="text-xs text-foreground-500">反馈邮箱</p>
                  <p className="text-sm text-foreground-200">contact@example.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-md border border-background-800 bg-background-950 px-4 py-3">
                <i className="ri-time-line text-lg text-accent-400" />
                <div>
                  <p className="text-xs text-foreground-500">回复时间</p>
                  <p className="text-sm text-foreground-200">工作日 48 小时内</p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-md border border-background-800 bg-background-950 px-4 py-3">
                <i className="ri-shake-hands-line text-lg text-accent-400" />
                <div>
                  <p className="text-xs text-foreground-500">合作洽谈</p>
                  <p className="text-sm text-foreground-200">欢迎通过表单留下联系方式</p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-background-800 bg-background-950 p-5">
            <h3 className="font-heading text-base font-bold text-foreground-50">意见反馈表单</h3>
            <p className="mt-1 mb-5 text-xs text-foreground-400">
              填写以下信息，我们会尽快查看并回复。
            </p>
            <ContactForm />
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1400px] px-4 pb-4 pt-12 md:px-8 md:pt-16">
        <h2 className="font-heading text-xl font-bold text-foreground-50 md:text-2xl">版权声明</h2>
        <div className="mt-4 space-y-3 rounded-lg border border-background-800 bg-background-900 p-6 text-sm leading-relaxed text-foreground-400 md:p-8">
          <p>
            本站为影视资料与推荐展示平台，所展示的影片名称、简介、演职员等资料仅用于信息参考与内容推荐，
            相关权利归各自权利人所有。
          </p>
          <p>
            本站不存储、不制作、不提供任何影视资源的下载与在线播放服务，页面中的播放区域仅用于界面演示。
            如相关内容涉及权益问题，请通过上方反馈表单与我们联系，我们将及时处理。
          </p>
          <p>© 2026 欧美精品在线观看 · 版权所有</p>
        </div>
      </section>
    </div>
  );
}